import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { ShellContext } from './layout/AppShell';
import { SectionCard, EmptyState, ConfirmDialog } from './ui';
import { TRUE_COLORS } from '../theme';

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data === 'string') {
    return ({
      'Invite already sent': 'Ya enviaste una invitación a ese correo.',
      'User already a member': 'Esa persona ya pertenece a una organización.',
      'Username already exists': 'Ese usuario ya existe. Elige otro.',
    } as Record<string, string>)[data] ?? data;
  }
  if (data?.response) return data.response;
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ');
  return fallback;
};

/** Small reusable form dialog with a list of text fields. */
const FormDialog = ({
  open, title, description, fields, submitLabel, onClose, onSubmit,
}: {
  open: boolean;
  title: string;
  description?: string;
  /** `options`: a choice (a select) instead of free text. */
  fields: { key: string; label: string; type?: string; required?: boolean; helperText?: string; initial?: string; options?: { value: string; label: string }[] }[];
  submitLabel: string;
  onClose: () => void;
  onSubmit: (values: Record<string, string>) => Promise<void>;
}) => {
  const [values, setValues] = React.useState<Record<string, string>>({});
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    if (open) { setValues(Object.fromEntries(fields.map(f => [f.key, f.initial ?? '']))); setError(''); setShowPassword(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const missing = fields.find(f => f.required && !values[f.key]?.trim());
    if (missing) { setError(`${missing.label} es obligatorio.`); return; }
    setBusy(true);
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError(errorText(err, 'No se pudo guardar.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ component: 'form', onSubmit: submit } as any}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && <DialogContentText sx={{ mb: 2 }}>{description}</DialogContentText>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack spacing={2} sx={{ pt: description ? 0 : 1 }}>
          {fields.map((f, i) => (
            f.options ? (
              <TextField key={f.key} select label={f.label} value={values[f.key] ?? f.initial ?? ''} helperText={f.helperText}
                onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}>
                {f.options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            ) : <TextField
              key={f.key}
              autoFocus={i === 0}
              label={f.label}
              required={f.required}
              type={f.type === 'password' && showPassword ? 'text' : f.type}
              helperText={f.helperText}
              value={values[f.key] ?? ''}
              onChange={e => setValues(v => ({ ...v, [f.key]: e.target.value }))}
              slotProps={f.type === 'password' ? {
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowPassword(s => !s)} aria-label="Mostrar contraseña">
                        {showPassword ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              } : undefined}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={onClose}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={busy}>{submitLabel}</Button>
      </DialogActions>
    </Dialog>
  );
};

// `login` is the name they sign in with at this business's TRUE POS (older servers only send the username).
const memberName = (m: any) => [m.name, m.lastname].filter(Boolean).join(' ') || m.login || m.member?.username || 'Sin nombre';

const OrganizationWidget = () => {
  const axios = useAxios();
  const navigate = useNavigate();
  const { membership } = React.useContext(ShellContext);

  const [organization, setOrganization] = React.useState<any>(null);
  const [locations, setLocations] = React.useState<any[]>([]);
  const [members, setMembers] = React.useState<any[]>([]);
  const [invites, setInvites] = React.useState<any[]>([]);
  const [refresh, setRefresh] = React.useState(0);
  const [dialog, setDialog] = React.useState<null | 'name' | 'location' | 'invite' | 'cashier'>(null);
  const [revoke, setRevoke] = React.useState<any | null>(null);
  const [notice, setNotice] = React.useState('');

  React.useEffect(() => {
    (async () => {
      const [o, l, m, i] = await Promise.allSettled([
        axios.get('management/organization/'),
        axios.get('api/locations/'),
        axios.get('management/members/'),
        axios.get('management/invites/'),
      ]);
      if (o.status === 'fulfilled') setOrganization(o.value.data);
      if (l.status === 'fulfilled') setLocations(l.value.data ?? []);
      if (m.status === 'fulfilled') setMembers(m.value.data ?? []);
      if (i.status === 'fulfilled') setInvites(i.value.data ?? []);
    })();
  }, [axios, refresh]);

  const isAdmin = !!membership?.is_admin;
  // Only the owner adds administrators; administrators add cashiers.
  const isOwner = !!membership?.is_patron;
  const ROLE_FIELD = { key: 'role', label: 'Rol', initial: 'cashier', options: [
    { value: 'cashier', label: 'Cajero: cobra en TRUE POS' },
    { value: 'admin', label: 'Administrador: también productos, inventario, promociones y equipo' },
  ] };

  return (
    <PageShell
      title={organization?.name ?? 'Organización'}
      subtitle={organization?.slug ? `Tus enlaces: receipt.com.mx/${organization.slug}/… · Sucursales, cajas y equipo` : 'Sucursales, cajas y el equipo que usa TRUE POS'}
      actions={isAdmin && <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={() => setDialog('name')}>Editar</Button>}
    >
      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 3 }}>{notice}</Alert>}

      <Stack spacing={4}>
        <Box>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h5">Sucursales</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Cada sucursal tiene su propio inventario y cajas.</Typography>
            </Box>
            {isAdmin && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog('location')}>Agregar sucursal</Button>}
          </Stack>
          <Grid container spacing={3}>
            {locations.map(loc => (
              <Grid key={loc.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card sx={{ height: '100%' }}>
                  <CardActionArea onClick={() => navigate(`/admin/locations/${loc.id}`)} sx={{ height: '100%', p: 2.5 }}>
                    <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: '12px', display: 'grid', placeItems: 'center', bgcolor: TRUE_COLORS.primary, color: '#fff', flexShrink: 0 }}>
                        <StorefrontOutlinedIcon />
                      </Box>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography sx={{ fontWeight: 700 }} noWrap>{loc.name}</Typography>
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary', mt: 0.5 }}>
                          <PlaceOutlinedIcon sx={{ fontSize: 16 }} />
                          <Typography variant="body2" noWrap sx={{ color: 'inherit' }}>{loc.address || 'Sin dirección'}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary', mt: 0.5 }}>
                          <PointOfSaleOutlinedIcon sx={{ fontSize: 16 }} />
                          <Typography variant="body2" sx={{ color: 'inherit' }}>
                            {(loc.checkouts ?? []).length} {(loc.checkouts ?? []).length === 1 ? 'caja' : 'cajas'}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <SectionCard
              title="Equipo"
              subtitle="Quienes pueden entrar a TRUE POS y a este panel"
              noPadding
              action={isAdmin && (
                <Stack direction="row" spacing={1}>
                  <Button size="small" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => setDialog('cashier')}>{isOwner ? 'Agregar persona' : 'Crear cajero'}</Button>
                  <Button size="small" variant="contained" startIcon={<ForwardToInboxOutlinedIcon />} onClick={() => setDialog('invite')}>Invitar</Button>
                </Stack>
              )}
            >
              {!isAdmin ? (
                <EmptyState compact title="Solo los administradores pueden ver al equipo" />
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Usuario</TableCell>
                        <TableCell>Rol</TableCell>
                        <TableCell align="right">Permisos</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {members.map(m => (
                        <TableRow key={m.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/members/${m.id}`)}>
                          <TableCell>
                            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                              <Avatar sx={{ width: 32, height: 32, fontSize: '0.75rem', fontWeight: 700, bgcolor: 'rgba(111,137,148,0.18)', color: TRUE_COLORS.primary }}>
                                {memberName(m).slice(0, 2).toUpperCase()}
                              </Avatar>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                {memberName(m)}{m.id === membership?.id && <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}> (tú)</Box>}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>{m.member?.email || m.login || m.member?.username}</TableCell>
                          <TableCell>{m.role || '—'}</TableCell>
                          <TableCell align="right">
                            {m.is_patron ? <Chip size="small" label="Dueño" sx={{ bgcolor: TRUE_COLORS.primary, color: '#fff' }} />
                              : m.is_admin ? <Chip size="small" label="Administrador" />
                                : <Chip size="small" variant="outlined" label="Cajero" />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </SectionCard>
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <SectionCard title="Invitaciones pendientes" noPadding>
              {invites.length === 0 ? (
                <EmptyState compact icon={<ForwardToInboxOutlinedIcon />} title="Sin invitaciones pendientes" />
              ) : (
                <List disablePadding>
                  {invites.map(inv => (
                    <ListItem
                      key={inv.id}
                      divider
                      secondaryAction={isAdmin && (
                        <Tooltip title="Revocar"><IconButton size="small" onClick={() => setRevoke(inv)}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                      )}
                    >
                      <ListItemText primary={inv.email} secondary="Esperando respuesta" primaryTypographyProps={{ fontWeight: 500, fontSize: '0.875rem', noWrap: true }} />
                    </ListItem>
                  ))}
                </List>
              )}
            </SectionCard>
          </Grid>
        </Grid>
      </Stack>

      <FormDialog
        open={dialog === 'name'}
        title="Nombre y enlace del negocio"
        fields={[
          { key: 'name', label: 'Nombre', required: true, initial: organization?.name },
          {
            key: 'slug', label: 'Identificador en tus enlaces', required: true, initial: organization?.slug,
            helperText: 'Aparece en tus enlaces: receipt.com.mx/identificador/pos, /menu… Minúsculas, números o guiones.',
          },
        ]}
        submitLabel="Guardar"
        onClose={() => setDialog(null)}
        onSubmit={async v => {
          await axios.put('management/organization/', { name: v.name.trim(), slug: v.slug.trim().toLowerCase(), palette: organization?.palette });
          setRefresh(r => r + 1);
        }}
      />
      <FormDialog
        open={dialog === 'location'}
        title="Nueva sucursal"
        description="Se crea con una caja lista para usarse en TRUE POS."
        fields={[{ key: 'name', label: 'Nombre', required: true }, { key: 'address', label: 'Dirección' }]}
        submitLabel="Crear sucursal"
        onClose={() => setDialog(null)}
        onSubmit={async v => {
          const { data } = await axios.post('api/locations/', { name: v.name.trim(), address: v.address.trim() });
          navigate(`/admin/locations/${data.id}`);
        }}
      />
      <FormDialog
        open={dialog === 'invite'}
        title="Invitar por correo"
        description="Le enviaremos un enlace para crear su cuenta y unirse a tu organización."
        fields={[{ key: 'email', label: 'Correo electrónico', type: 'email', required: true }, ...(isOwner ? [ROLE_FIELD] : [])]}
        submitLabel="Enviar invitación"
        onClose={() => setDialog(null)}
        onSubmit={async v => {
          await axios.post('management/send-invite/', { email: v.email.trim(), is_admin: v.role === 'admin' });
          setNotice(`Invitación enviada a ${v.email.trim()}${v.role === 'admin' ? ' como administrador' : ''}.`);
          setRefresh(r => r + 1);
        }}
      />
      <FormDialog
        open={dialog === 'cashier'}
        title={isOwner ? 'Agregar a tu equipo' : 'Crear cuenta de cajero'}
        description={`Con usuario y contraseña, para quien no tiene correo: inicia sesión en TRUE POS${organization?.slug ? ` (receipt.com.mx/${organization.slug}/pos)` : ''}${isOwner ? ' y, si es administrador, también en este panel' : ''}.`}
        fields={[
          { key: 'username', label: 'Usuario', required: true, helperText: 'Solo tiene que ser único en tu negocio, p. ej. Martin o caja.centro' },
          { key: 'password', label: 'Contraseña', type: 'password', required: true, helperText: 'Mínimo 8 caracteres' },
          ...(isOwner ? [ROLE_FIELD] : []),
        ]}
        submitLabel="Crear cuenta"
        onClose={() => setDialog(null)}
        onSubmit={async v => {
          if (v.password.length < 8) throw Object.assign(new Error('short password'), { response: { data: 'La contraseña debe tener al menos 8 caracteres.' } });
          const { data } = await axios.post('management/add-member/', { username: v.username.trim(), password: v.password, is_admin: v.role === 'admin' });
          const login = data?.login || v.username.trim();
          const business = data?.business || organization?.slug;
          setNotice(business
            ? `Cuenta "${login}" creada${data?.is_admin ? ' como administrador' : ''}. Inicia sesión en receipt.com.mx/${business}/pos con el usuario ${login}; en una computadora nueva${data?.is_admin ? ' o en este panel' : ''}, con ${login}@${business}.`
            : `Cuenta "${login}" creada. Ya puede iniciar sesión en TRUE POS.`);
          setRefresh(r => r + 1);
        }}
      />
      <ConfirmDialog
        open={!!revoke}
        danger
        title="¿Revocar invitación?"
        description={`El enlace enviado a ${revoke?.email} dejará de funcionar.`}
        confirmLabel="Revocar"
        onClose={() => setRevoke(null)}
        onConfirm={async () => {
          await axios.delete(`management/invites/${revoke.id}/`);
          setRevoke(null);
          setRefresh(r => r + 1);
        }}
      />
    </PageShell>
  );
};

export default OrganizationWidget;
